import { useCallback, useEffect, useState } from "react";

import { useViewer } from "@/hooks/use-viewer";
import {
  ADVERTISING_CONSENT_CHANGE_EVENT,
  ADVERTISING_CONSENT_STORAGE_KEY,
  type AdvertisingConsentChoice,
} from "@/lib/advertisingConsent";
import { persistAdvertisingConsent } from "@/lib/persistAdvertisingConsent";
import { readAdvertisingConsent } from "@/lib/readAdvertisingConsent";

export function useAdvertisingConsent() {
  const { advertisingConsent } = useViewer();
  const [choice, setChoice] = useState<AdvertisingConsentChoice | null>(advertisingConsent);

  useEffect(() => {
    const syncChoice = () => {
      setChoice(readAdvertisingConsent() ?? advertisingConsent);
    };
    const syncStoredChoice = (event: StorageEvent) => {
      if (event.key === ADVERTISING_CONSENT_STORAGE_KEY) syncChoice();
    };

    const storedChoice = readAdvertisingConsent();
    if (advertisingConsent === null && storedChoice !== null) {
      persistAdvertisingConsent(storedChoice);
    }
    syncChoice();
    window.addEventListener(ADVERTISING_CONSENT_CHANGE_EVENT, syncChoice);
    window.addEventListener("storage", syncStoredChoice);
    return () => {
      window.removeEventListener(ADVERTISING_CONSENT_CHANGE_EVENT, syncChoice);
      window.removeEventListener("storage", syncStoredChoice);
    };
  }, [advertisingConsent]);

  const choose = useCallback(
    (nextChoice: AdvertisingConsentChoice) => {
      if (choice === nextChoice) return;
      persistAdvertisingConsent(nextChoice);
      setChoice(nextChoice);
    },
    [choice],
  );

  return { choice, choose } as const;
}
